import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  Edit,
  Trash2,
  X,
  ExternalLink,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  Image as ImageIcon
} from 'lucide-react'
import { adminBannersService } from '../services'
import './Banners.css'

const Banners = () => {
  const [banners, setBanners] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [selectedBanner, setSelectedBanner] = useState(null)
  const reorderBusyRef = useRef(false)
  /** Monotonic load generation so an older in-flight request cannot overwrite a newer result. */
  const loadGenRef = useRef(0)

  /** @param {{ silent?: boolean }} [opts] - silent=true: no full-page loader (keeps list visible). */
  const loadBanners = async (opts = {}) => {
    const silent = Boolean(opts.silent)
    const gen = ++loadGenRef.current
    try {
      if (!silent) {
        setLoading(true)
        setLoadError(null)
      }
      const data = await adminBannersService.list()
      if (gen !== loadGenRef.current) return
      setBanners(Array.isArray(data) ? data : [])
    } catch (err) {
      if (gen !== loadGenRef.current) return
      console.error('Failed to load banners:', err)
      if (!silent) {
        const msg =
          err.response?.data?.error ??
          (err.code === 'ECONNABORTED'
            ? 'Request timed out. Check API URL and network.'
            : null) ??
          err.message ??
          'Failed to load banners'
        setLoadError(String(msg))
      }
    } finally {
      if (!silent && gen === loadGenRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    loadBanners()
  }, [])

  const handleAddBanner = () => {
    setSelectedBanner(null)
    setShowModal(true)
  }

  const handleEditBanner = (banner) => {
    setSelectedBanner(banner)
    setShowModal(true)
  }

  const handleDeleteBanner = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return
    try {
      await adminBannersService.delete(id)
      setBanners((prev) => prev.filter((b) => b.id !== id))
    } catch (e) {
      console.error(e)
    }
  }

  const handleToggleStatus = async (banner) => {
    try {
      const updated = await adminBannersService.update(banner.id, { isActive: !banner.isActive })
      if (updated)
        setBanners((prev) => prev.map((b) => (b.id === banner.id ? { ...b, ...updated } : b)))
    } catch (e) {
      console.error(e)
    }
  }

  const handleMove = async (index, direction) => {
    if (reorderBusyRef.current) return
    const newIndex = index + direction

    let updates = null
    setBanners((prev) => {
      if (newIndex < 0 || newIndex >= prev.length) return prev
      const next = [...prev]
      const temp = next[index]
      next[index] = next[newIndex]
      next[newIndex] = temp
      updates = [
        { id: next[index].id, orderIndex: index },
        { id: next[newIndex].id, orderIndex: newIndex },
      ]
      return next
    })

    if (!updates) return

    reorderBusyRef.current = true
    try {
      await Promise.all(
        updates.map((u) => adminBannersService.update(u.id, { orderIndex: u.orderIndex }))
      )
    } catch (e) {
      console.error('Failed to update order:', e)
      await loadBanners({ silent: true })
    } finally {
      reorderBusyRef.current = false
    }
  }

  return (
    <div className="banners-page">
      <div className="page-header">
        <div className="header-titles">
          <h1>Banner Management</h1>
          <p>Control the promotional banners displayed on the mobile app's home screen.</p>
        </div>
      </div>

      <div className="page-actions">
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn-secondary"
          style={{ marginRight: 8 }}
          disabled={loading}
          onClick={() => loadBanners()}
        >
          Refresh list
        </button>
        <button className="btn-primary" onClick={handleAddBanner}>
          <Plus size={18} />
          Add Banner
        </button>
      </div>

      <div className="banners-list">
        {loading ? (
          <div className="loading-state">
            <p>Loading banners...</p>
          </div>
        ) : loadError && banners.length === 0 ? (
          <div className="empty-state">
            <ImageIcon size={48} />
            <h3>Could not load banners</h3>
            <p style={{ marginBottom: 8 }}>{loadError}</p>
            <p style={{ fontSize: 14, opacity: 0.85, marginBottom: 16 }}>
              Dev: restart <code>npm run dev</code> after changing env; ensure IN-Backend is running
              (port 3000). If you see &quot;Network Error&quot;, check <code>VITE_API_URL</code> matches
              where the API listens and set <code>VITE_ADMIN_KEY</code> when the server uses{' '}
              <code>ADMIN_KEY</code>.
            </p>
            <button type="button" className="btn-primary" onClick={() => loadBanners()}>
              Retry
            </button>
          </div>
        ) : banners.length === 0 ? (
          <div className="empty-state">
            <ImageIcon size={48} />
            <h3>No banners yet</h3>
            <p>Create your first banner to display on the mobile app.</p>
            <button className="btn-primary" onClick={handleAddBanner}>
              Add Banner
            </button>
          </div>
        ) : (
          <div className="banner-cards">
            {banners.map((banner, index) => (
              <BannerCard
                key={banner.id}
                banner={banner}
                index={index}
                total={banners.length}
                onEdit={() => handleEditBanner(banner)}
                onDelete={() => handleDeleteBanner(banner.id)}
                onToggle={() => handleToggleStatus(banner)}
                onMove={handleMove}
              />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <BannerEditorModal
          key={selectedBanner ? selectedBanner.id : 'new'}
          banner={selectedBanner}
          onClose={() => setShowModal(false)}
          onSave={async (bannerData) => {
            try {
              if (selectedBanner) {
                const updated = await adminBannersService.update(selectedBanner.id, bannerData)
                if (updated)
                  setBanners((prev) =>
                    prev.map((b) => (b.id === selectedBanner.id ? { ...b, ...updated } : b))
                  )
              } else {
                const created = await adminBannersService.create({
                  ...bannerData,
                  orderIndex: banners.length,
                })
                if (created) setBanners((prev) => [...prev, created])
              }
              setShowModal(false)
            } catch (e) {
              console.error(e)
              alert('Failed to save banner. Please try again.')
              throw e
            }
          }}
        />
      )}
    </div>
  )
}

const BannerCard = ({ banner, index, total, onEdit, onDelete, onToggle, onMove }) => {
  return (
    <div className={`banner-card ${!banner.isActive ? 'inactive' : ''}`}>
      <div className="banner-preview">
        <img src={banner.imageUrl} alt={banner.title || 'Banner'} />
        <div className="banner-status-badge">
          {banner.isActive ? <span className="status-active">Active</span> : <span className="status-inactive">Inactive</span>}
        </div>
      </div>
      <div className="banner-content">
        <div className="banner-info">
          <h3 className="banner-title">{banner.title || 'Untitled Banner'}</h3>
          {banner.linkUrl && (
            <div className="banner-link">
              <ExternalLink size={12} />
              <span>{banner.linkUrl}</span>
            </div>
          )}
        </div>
        <div className="banner-actions">
          <div className="order-actions">
            <button
              className="action-btn"
              disabled={index === 0}
              onClick={() => onMove(index, -1)}
              title="Move Up"
            >
              <ChevronUp size={16} />
            </button>
            <button
              className="action-btn"
              disabled={index === total - 1}
              onClick={() => onMove(index, 1)}
              title="Move Down"
            >
              <ChevronDown size={16} />
            </button>
          </div>
          <div className="crud-actions">
            <button className="action-btn" onClick={onToggle} title={banner.isActive ? 'Deactivate' : 'Activate'}>
              {banner.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
            <button className="action-btn" onClick={onEdit} title="Edit">
              <Edit size={16} />
            </button>
            <button className="action-btn danger" onClick={onDelete} title="Delete">
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const BannerEditorModal = ({ banner, onClose, onSave }) => {
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    title: banner?.title || '',
    imageUrl: banner?.imageUrl || '',
    isActive: banner?.isActive ?? true,
  })

  useEffect(() => {
    setSaving(false)
    setFormData({
      title: banner?.title || '',
      imageUrl: banner?.imageUrl || '',
      isActive: banner?.isActive ?? true,
    })
  }, [banner])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.imageUrl.trim() || saving) return
    setSaving(true)
    try {
      await onSave({
        ...formData,
        linkUrl: '',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!saving) onClose()
      }}
    >
      <div className="modal" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{banner ? 'Edit Banner' : 'Create Banner'}</h2>
          <button type="button" className="modal-close" disabled={saving} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Title (Optional)</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g. Summer Special"
                disabled={saving}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Banner Image *</label>
              <div className="banner-upload-area">
                {!formData.imageUrl ? (
                  <label className={`upload-label${saving ? ' upload-label-disabled' : ''}`}>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      disabled={saving}
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (file) {
                          const reader = new FileReader()
                          reader.onloadend = () => {
                            setFormData((prev) => ({ ...prev, imageUrl: reader.result }))
                          }
                          reader.readAsDataURL(file)
                        }
                      }}
                    />
                    <div className="upload-placeholder">
                      <Plus size={24} />
                      <span>Upload Image</span>
                    </div>
                  </label>
                ) : (
                  <div className="upload-preview-container">
                    <img src={formData.imageUrl} alt="Banner preview" />
                    <button
                      type="button"
                      className="remove-image-btn"
                      disabled={saving}
                      onClick={() => setFormData({ ...formData, imageUrl: '' })}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  disabled={saving}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span>Active (visible on mobile)</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={!formData.imageUrl.trim() || saving}
            >
              {saving ? 'Please wait…' : banner ? 'Save Changes' : 'Create Banner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Banners
